import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import type { NotificationRow as Row } from "@/api/notifications";
import {
  relativeTime,
  resolveDeepLink,
  rowTile,
  SOURCE_LABELS,
  WORK_SURFACE_META,
} from "@/lib/notificationMeta";

/**
 * D-043 S2 — one notification row, shared by the bell dropdown (size="sm")
 * and the Center (size="lg").
 *
 * Legacy rows (kind="legacy", cCEventType=null) render from the stock
 * projection: neutral tile, no workSurface/source badges — never blank
 * (AC-022). Deep-link CTA hidden when the pattern can't be resolved to a
 * live SPA route; ACL re-check happens at the destination (fetch → 403 →
 * ForbiddenState, app convention).
 */
export function NotificationRow({
  row,
  size,
  onActivate,
  onMarkRead,
}: {
  row: Row;
  size: "sm" | "lg";
  /** Called after navigation (e.g. close the dropdown). */
  onActivate?: () => void;
  onMarkRead?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const { Icon, tileClass } = rowTile(row);
  // D-137b W3 — pass source metadata so the belt can rewrite a legacy meeting-source
  // /clients/{accountId} link to /meetings?open={CCI-id} (sourceEntityId), avoiding the
  // 404 type-mismatch. Genuine client links (sourceEntityType≠CCI) are left intact.
  const link = resolveDeepLink(row.deepLink, {
    sourceEntityType: row.sourceEntityType,
    sourceEntityId: row.sourceEntityId,
  });
  const surface = row.workSurface ? WORK_SURFACE_META[row.workSurface] : undefined;
  const sourceLabel = row.sourceModule ? SOURCE_LABELS[row.sourceModule] : undefined;

  const open = () => {
    if (!row.read) onMarkRead?.(row.id);
    if (link) {
      navigate(link);
      onActivate?.();
    }
  };

  const sm = size === "sm";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      data-notification-id={row.id}
      data-kind={row.kind}
      className={cn(
        "relative flex w-full cursor-pointer gap-3 border-b border-border text-left transition-colors last:border-b-0",
        "hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
        sm ? "px-3.5 py-2.5" : "min-h-[var(--density-row-min)] px-5 py-3.5",
        !row.read && "bg-primary/[0.04]",
      )}
    >
      {/* unread affordance: 3px brand stripe + dot */}
      {!row.read && (
        <>
          <span className="absolute inset-y-0 left-0 w-[3px] bg-primary" aria-hidden="true" />
          <span
            className={cn(
              "absolute left-[7px] h-1.5 w-1.5 rounded-full bg-primary",
              sm ? "top-4" : "top-6",
            )}
            aria-hidden="true"
          />
        </>
      )}

      <span
        className={cn(
          "grid shrink-0 place-items-center rounded-badge",
          sm ? "h-[30px] w-[30px]" : "h-9 w-9",
          tileClass,
        )}
        aria-hidden="true"
      >
        <Icon className={sm ? "h-4 w-4" : "h-[18px] w-[18px]"} />
      </span>

      <div className="min-w-0 flex-1">
        <div className={cn("font-semibold text-foreground", sm ? "text-[13px]" : "text-sm")}>
          {row.title}
        </div>
        {row.subtitle && (
          <div className={cn("truncate text-muted-foreground", sm ? "text-xs" : "text-[13px]")}>
            {row.subtitle}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {sourceLabel && (
            <span className="rounded-full border border-border bg-muted px-2 py-px text-[10px] font-semibold text-muted-foreground">
              {sourceLabel}
            </span>
          )}
          {!sm && surface && (
            <span
              className={cn(
                "rounded-full px-2 py-px text-[10px] font-semibold",
                surface.className,
              )}
            >
              {surface.label}
            </span>
          )}
          {!sm && row.kind === "legacy" && (
            <span className="text-[10px] italic text-muted-foreground/70">
              stock-сповіщення
            </span>
          )}
          {!sm && row.redacted && (
            <span className="text-[10px] italic text-muted-foreground/70">
              деталі за посиланням
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-2">
        <span className="whitespace-nowrap text-[11px] text-muted-foreground/70">
          {relativeTime(row.createdAt)}
        </span>
        {!sm && link && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            className="rounded-badge bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Відкрити
          </button>
        )}
      </div>
    </div>
  );
}
